// --- Core Dependencies
import React from 'react';
import { useSelector } from 'react-redux';
// --- UI Components
import Button from '@dtci/spellbook/Button';
import { DetailsModal } from '../../../../../components/DetailsModal';
import { SharedSectionForm } from './form';
// --- API and State Management
import { datastageApi } from '../../../../../api';
import { useSharedSectionsActions } from '../../../../../store/sharedSections/useSharedSections';
// --- Utilities
import { useLoading } from '../../../../../utils/hooks/useLoading';
import { filterCriteria } from '../../../../../utils/filterCriteria';

/**
 * SharedSectionModal Component
 * Renders a modal for creating or editing shared sections.
 *
 * @returns {JSX.Element} The Shared Section Modal component.
 */
export const SharedSectionModal = () => {
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { filterForm, sectionRange, sectionSort, isSSModalOpen, modalMode, section } = useSelector(
        (state) => state.sharedSections,
    );
    const { updateSharedSections } = useSharedSectionsActions();

    const fetchSharedSections = async (filters = {}) => {
        try {
            setLoadingTrue();
            const data = await datastageApi.sharedSections(
                filterCriteria(filters, '', 'sharedSections'),
                sectionRange,
                sectionSort,
            );
            updateSharedSections({
                shared: data?.content,
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable?.pageNumber,
            });
        } catch (error) {
            console.error('Error fetching shared sections:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Close the modal and resets the state
    const onCloseModal = async () => {
        updateSharedSections({ isSSModalOpen: false });
        await fetchSharedSections(filterForm);
    };

    // Save the shared section and refresh grid data
    const handleSave = async () => {
        setLoadingTrue();
        try {
            // Update the shared section via API
            await datastageApi.updateSharedSection(section);

            // Fetch updated shared sections data for the grid
            const data = await datastageApi.sharedSections(
                filterCriteria(filterForm, '', 'sharedSections'),
                sectionRange,
                sectionSort,
            );

            // Update shared section state with the new data
            updateSharedSections({
                modalMode: 'edit',
                shared: data?.content,
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable?.pageNumber,
            });
        } finally {
            setLoadingFalse();
        }
    };

    // Set the modal header based on the mode (edit or create)
    const modalHeader = modalMode === 'edit' ? 'Edit Shared Section' : 'Create Shared Section';

    return (
        <DetailsModal
            isOpen={isSSModalOpen}
            onClose={onCloseModal}
            modalHeader={modalHeader}
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    {/* Left side - Delete button */}
                    <Button
                        disabled={modalMode === 'create' || section?.productIdList?.length > 0}
                        sizeType="small"
                        styleType="link"
                        iconType="fa-solid fa-trash"
                        onClick={onCloseModal}
                    >
                        Delete
                    </Button>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {/* Right side - Cancel and Save buttons */}
                        <Button
                            sizeType="small"
                            styleType="link"
                            iconType="fa-solid fa-ban"
                            onClick={onCloseModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            sizeType="small"
                            styleType="link"
                            iconType="fa-solid fa-floppy-disk"
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            }
        >
            {/* Form for shared section details */}
            <SharedSectionForm />
        </DetailsModal>
    );
};
